import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Mail } from 'lucide-react';
import { useCutierStore } from '../../store/useCutierStore';
import { queueSeminarMail, saveCutierAdminSettings } from '../../services/cutierFirebaseService';
import { downloadCutierInvoicePdf } from '../../utils/cutier/cutierInvoicePdfGenerator';
import { buildSeminarInviteMail } from '../../utils/cutier/cutierInviteMail';

const inputClass =
  'w-full rounded-xl border border-pe-border-strong bg-pe-input px-3 py-2.5 text-sm text-pe-text focus:border-[#E6C299] focus:outline-none';

const CutierAdminPanel: React.FC = () => {
  const selectedMonth = useCutierStore((s) => s.selectedMonth);
  const rateGross = useCutierStore((s) => s.rateGross);
  const seminarUrl = useCutierStore((s) => s.seminarUrl);
  const seminarPassword = useCutierStore((s) => s.seminarPassword);
  const invoiceProfile = useCutierStore((s) => s.invoiceProfile);
  const setAdminSettings = useCutierStore((s) => s.setAdminSettings);

  const [rateInput, setRateInput] = useState(String(rateGross));
  const [urlInput, setUrlInput] = useState(seminarUrl);
  const [passwordInput, setPasswordInput] = useState(seminarPassword);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setRateInput(String(rateGross));
    setUrlInput(seminarUrl);
    setPasswordInput(seminarPassword);
  }, [rateGross, seminarPassword, seminarUrl]);

  const preview = useMemo(
    () =>
      buildSeminarInviteMail({
        email: testEmail.trim() || 'test@cutier.local',
        name: 'Test',
        seminarUrl: urlInput.trim() || seminarUrl,
        seminarPassword: passwordInput.trim(),
      }),
    [passwordInput, seminarUrl, testEmail, urlInput]
  );

  const persistSettings = async () => {
    const parsed = Number(String(rateInput).replace(',', '.'));
    const url = urlInput.trim();
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setFeedback({ type: 'error', text: 'Bitte einen gültigen Brutto-Betrag eingeben.' });
      return null;
    }
    if (!url) {
      setFeedback({ type: 'error', text: 'Bitte einen Seminar-Link eingeben.' });
      return null;
    }
    const settings = {
      rateGross: parsed,
      seminarUrl: url,
      seminarPassword: passwordInput.trim(),
    };
    setAdminSettings(settings);
    const result = await saveCutierAdminSettings(settings);
    return result;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setFeedback(null);
    try {
      const result = await persistSettings();
      if (!result) return;
      setFeedback({
        type: 'success',
        text: result.savedRemote
          ? 'Vergütung, Seminar-Link und Passwort gespeichert.'
          : 'Lokal gespeichert. Firestore-Write noch blockiert (Rules) — Vorschau und Test reichen damit.',
      });
    } catch {
      setFeedback({ type: 'error', text: 'Speichern fehlgeschlagen.' });
    } finally {
      setBusy(false);
    }
  };

  const handleTestPdf = () => {
    const parsed = Number(String(rateInput).replace(',', '.'));
    void downloadCutierInvoicePdf({
      monthKey: selectedMonth,
      leads: [],
      profile: invoiceProfile,
      rateGross: Number.isFinite(parsed) && parsed > 0 ? parsed : rateGross,
      isTest: true,
      testParticipantCount: 3,
    });
  };

  const handleTestMail = async () => {
    const email = testEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      setFeedback({ type: 'error', text: 'Bitte deine E-Mail für den Testversand eintragen.' });
      return;
    }
    setMailBusy(true);
    setFeedback(null);
    try {
      await persistSettings();
      const ok = await queueSeminarMail(email, 'Test');
      setFeedback({
        type: ok ? 'success' : 'error',
        text: ok
          ? 'Mail ist raus. Bitte Posteingang und Spam prüfen — ohne Change-Radar.'
          : 'Versand fehlgeschlagen. Bitte Seite neu laden und nochmal senden.',
      });
    } catch {
      setFeedback({ type: 'error', text: 'Test-Mail fehlgeschlagen.' });
    } finally {
      setMailBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-[#E6C299]/40 bg-pe-card p-6">
      <h2 className="text-sm font-black uppercase tracking-wider text-pe-accent-text">Admin</h2>
      <p className="mt-1 text-xs text-pe-muted">
        Vergütung, Seminar-Link und Zugangspasswort. Die Einladungs-Mail nutzt genau diese Werte.
      </p>
      <form onSubmit={handleSave} className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
            Vergütung brutto je Teilnehmer (€)
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={rateInput}
            onChange={(e) => setRateInput(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
            Seminar-Passwort (steht in der Mail)
          </span>
          <input
            type="text"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="block md:col-span-2">
          <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
            Seminar-Link
          </span>
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://"
            className={inputClass}
          />
        </label>
        <div className="flex flex-wrap gap-2 md:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-[#E6C299] px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-[#030303] hover:bg-[#d4b086] disabled:opacity-50"
          >
            {busy ? 'Speichere…' : 'Speichern'}
          </button>
          <button
            type="button"
            onClick={handleTestPdf}
            className="inline-flex items-center gap-2 rounded-xl border border-pe-border-strong bg-pe-input px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-pe-text hover:text-white"
          >
            <FileText className="h-4 w-4" />
            Test-Rechnung PDF
          </button>
        </div>
      </form>

      <div className="mt-6 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="block min-w-0 flex-1">
            <span className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-pe-muted">
              Testversand (ohne Lead / ohne 50 €) — Antworten an rd@ramindell.com
            </span>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="deine@mail.de"
              className={inputClass}
            />
          </label>
          <button
            type="button"
            onClick={() => void handleTestMail()}
            disabled={mailBusy}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-pe-border-strong bg-pe-input px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-pe-text hover:text-white disabled:opacity-50"
          >
            <Mail className="h-4 w-4" />
            {mailBusy ? 'Sende Test…' : 'Test-Mail an mich'}
          </button>
        </div>
        <p className="text-xs leading-relaxed text-pe-muted">
          Die Vorschau unten ist der komplette Mailinhalt — Screenshot fürs Team-OK. Echter Versand erst nach Deploy von Rules und Worker.
        </p>
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-pe-muted">
            Mail-Vorschau (vollständig)
          </p>
          <div className="rounded-xl border border-pe-border-strong bg-[#030303] p-2">
            <div dangerouslySetInnerHTML={{ __html: preview.html }} />
          </div>
        </div>
      </div>

      {feedback ? (
        <div
          className={
            feedback.type === 'success'
              ? 'mt-4 rounded-lg border border-emerald-800 bg-emerald-950/40 p-3 text-sm text-emerald-400'
              : 'mt-4 rounded-lg border border-red-800 bg-red-950/40 p-3 text-sm text-red-300'
          }
        >
          {feedback.text}
        </div>
      ) : null}
    </div>
  );
};

export default CutierAdminPanel;
